import { useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'

// Components
import { If, Text, OutreachButton, Unless } from '../../../..'

// Selectors
import {
  selectDmFailure,
  selectDmRequest,
  selectDmSuccess,
} from '../../../../../store/selectors/homeowner'
import {
  selectCustomerFirstName,
  selectCustomerIsInsuranceAgent,
} from '../../../../../store/selectors/customerProfile'
import { selectDaysUntilRenewal } from '../../../../../store/selectors/homeInsurance'

// Actions
import { sendRequestForDirectMessage } from '../../../../../actions/homeowner'

// Constants
import { SYSTEM_REQUEST } from '../../../../../constants/messages'
import { HOME_INSURANCE_TOPIC_KEY } from '../../../../../constants/topicKeys'

// Assets
import alarmClockImg from './alarmClock.png'

// Styles
import styles from './GetHelpSection.module.scss'

const buttonMessages = defineMessages({
  buttonText: {
    id: 'Homeowner.homeInsurance.helpWithThisBtn',
    defaultMessage: 'Get help with this',
  },
  buttonSuccess: {
    id: 'Homeowner.homeInsurance.helpWithThisBtn.success',
    defaultMessage: 'Message sent!',
  },
  buttonFailure: {
    id: 'Homeowner.homeInsurance.helpWithThisBtn.failure',
    defaultMessage: 'Please try again later!',
  },
})

const GetHelpSection = () => {
  const dispatch = useDispatch()
  const customerFirstName = useSelector(selectCustomerFirstName)
  const days = useSelector(selectDaysUntilRenewal)
  const dmFailure = useSelector(selectDmFailure)
  const dmRequest = useSelector(selectDmRequest)
  const dmSuccess = useSelector(selectDmSuccess)
  const isInsuranceAgent = useSelector(selectCustomerIsInsuranceAgent)
  const intl = useIntl()
  const { ampli } = useContext(AmplitudeContext)

  const handleClick = () => {
    const message = intl.formatMessage(SYSTEM_REQUEST.homeInsuranceMessage)
    const topic = intl.formatMessage(SYSTEM_REQUEST.homeInsuranceTopic)
    dispatch(
      sendRequestForDirectMessage(
        message,
        topic,
        HOME_INSURANCE_TOPIC_KEY,
        ampli
      )
    )
  }

  return (
    <Text
      data-qa='get-help-with-this-section'
      element='div'
      align='center'
      variant='neutralCool'
      variantCount='900'
      className={styles.border}
    >
      <div className={styles.alarmClockContainer}>
        <img
          className={styles.alarmClock}
          src={alarmClockImg}
          alt='alarmClock'
        />
      </div>

      <div className={styles.body}>
        <Text
          className={styles.title}
          variant='neutralCool'
          variantCount='900'
          element='div'
        >
          <FormattedMessage
            id='Homeowner.homeInsurance.policyLikelyRenewsIn'
            defaultMessage='Your policy likely renews in {days}'
            values={{
              days: (
                <Text
                  element='span'
                  weight='bold'
                  variant='neutralCool'
                  variantCount='900'
                >
                  {days}{' '}
                  <FormattedMessage
                    id='Homeowner.homeInsurance.policyLikelyRenewsInDays'
                    defaultMessage='days'
                  />
                </Text>
              ),
            }}
          />
        </Text>
        <Text element='div' variant='neutralCool' variantCount='700' size='s'>
          <If condition={isInsuranceAgent}>
            <FormattedMessage
              id='Homeowner.homeInsurance.helpWithThisWithInsuranceAgent'
              defaultMessage='Now may be a good time to review your costs and coverage by asking {insuranceAgentFirstName}.'
              values={{
                insuranceAgentFirstName: customerFirstName,
              }}
            />
          </If>
          <Unless condition={isInsuranceAgent}>
            <FormattedMessage
              id='Homeowner.homeInsurance.helpWithThis'
              defaultMessage='Now may be a good time to review your costs and coverage by checking with your insurance agent or provider.'
            />
          </Unless>
        </Text>
      </div>

      <OutreachButton
        className={styles.outReachBtn}
        handleClick={handleClick}
        success={dmSuccess}
        failure={dmFailure}
        requested={dmRequest}
        buttonMessage={buttonMessages.buttonText}
        resultMessage={
          dmSuccess
            ? intl.formatMessage(buttonMessages.buttonSuccess)
            : intl.formatMessage(buttonMessages.buttonFailure)
        }
      />
    </Text>
  )
}

export default GetHelpSection
